import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableContainer, Tooltip } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  StyledTableHeader,
  StyledTableRowHeader,
  StyledTableCellHeader,
  StyledTableCell,
} from '../PorfolioTableShared.Styles';
import { StyledTableRow } from '../PortfolioSimpleIndicatorTable/PortfolioSimpleIndicatorTable.Styles';
import { sortTablesByType } from '../../../services/sortsUtil';
import NumberFormat from 'react-number-format';
import * as config from '../../../api/config.json';
import { ProjectsListSkeleton } from '../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';
import { ExcelDownloadButton } from '../../Util/ExcelDownloadButton/ExcelDownloadButton';
import useDataNormalize from '../../../hooks/useDataNormalize';
import usePaginationSort from '../../../hooks/usePaginationSort';
import PaginationTable from '../PaginationTable/PaginationTable';
import { Pagination } from '@material-ui/lab';
import { ReactComponent as IconSort } from '../../../assets/icons/u_sort.svg';
import { withStyles } from '@material-ui/core/styles';
import { FilterTables } from '../../Filter/FilterTables/FilterTables';
import { KnowledgeProductsYearFilterContext } from '../../../context/KnowledgeProductsYearFilterContext';
import { updateCurrentIndicatorFromDetail } from '../../../redux/actions/currentIndicator';
import {
  extractYearFromExpectedApprovalDate,
  filterProjectsInPreparationByHeader,
  getProjectsInPreparationPipelineCounts,
  normalizePipeline,
} from '../../../services/projectsInPreparationUtil';
import FiltersErrorMessage from '../../Util/Errors/FiltersErrorMessage/FiltersErrorMessage';
import SearchTabErrorMessage from '../../Util/Errors/SearchTabErrorMessage/SearchTabErrorMessage';

const BlackTooltip = withStyles({
  tooltip: {
    backgroundColor: '#101319',
    fontSize: '16px',
    fontFamily: 'Rubik',
    fontWeight: '400',
    lineHeight: '20px',
    padding: '8px 12px',
    borderRadius: '8px',
  },
  arrow: {
    fontSize: 20,
    color: '#4A4A4A',
    '&::before': {
      backgroundColor: '#101319',
    },
  },
})(Tooltip);

type Props = {
  projects: any[];
  tableInfo: any[];
  loading?: boolean;
};

const headers = [
  'id',
  'last_project',
  'name',
  'division',
  'amount_bid_usd',
  'gcm_team_leader',
  'status',
  'year',
  'Pipeline',
  ' ',
];

const selectedKeys = ['id', 'code', 'title', 'field1', 'field2', 'field3', 'field7', 'field5', 'field6'];

const ProjectsInPreparationTable = ({ projects, tableInfo, loading }: Props) => {
  const dispatch = useDispatch();
  const currentIndicator = useSelector<any, any>(state => state.currentIndicator);
  const [sortAsc, setSortAsc] = useState(true);
  const { t } = useTranslation();
  const [isLoading, setisLoading] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [dataFilter, setDataFilter] = useState<any>(projects);
  const {
    selectedPreparationYear,
    setSelectedPreparationYear,
    setAvailablePreparationYears,
    selectedPipelineId,
    setSelectedPipelineId,
    availablePipelines,
    setAvailablePipelines,
  } = useContext(KnowledgeProductsYearFilterContext);

  const selectedPipelineLabel = useMemo(
    () => availablePipelines.find(pipeline => pipeline.id === selectedPipelineId)?.label || '',
    [availablePipelines, selectedPipelineId],
  );

  const headerFilteredProjects = useMemo(
    () => filterProjectsInPreparationByHeader(projects, selectedPreparationYear, selectedPipelineLabel),
    [projects, selectedPipelineLabel, selectedPreparationYear],
  );

  const pipelineCounts = useMemo(
    () => getProjectsInPreparationPipelineCounts(headerFilteredProjects),
    [headerFilteredProjects],
  );

  const filteredProjectsByCustomFilters = useMemo(() => {
    return headerFilteredProjects.filter(project => {
      if (searchValue.length < 3) {
        return true;
      }

      return selectedKeys.some(key => {
        const fieldValue = project[key];
        return fieldValue && fieldValue.toString().toLowerCase().includes(searchValue.toLowerCase());
      });
    });
  }, [headerFilteredProjects, searchValue]);

  useEffect(() => {
    const years = Array.from(
      new Set(
        projects
          .map(project => extractYearFromExpectedApprovalDate(project.field7))
          .filter((year): year is number => year !== null),
      ),
    ).sort((a, b) => b - a);

    setAvailablePreparationYears(years);

    if (selectedPreparationYear !== 0 && !years.includes(selectedPreparationYear)) {
      setSelectedPreparationYear(0);
    }
  }, [projects, selectedPreparationYear, setAvailablePreparationYears, setSelectedPreparationYear]);

  useEffect(() => {
    const pipelines = Array.from(
      new Set(projects.map(project => normalizePipeline(project.field6)).filter(Boolean)),
    )
      .sort((a, b) => a.localeCompare(b))
      .map((label, index) => ({ id: index + 1, label }));

    setAvailablePipelines(pipelines);

    if (selectedPipelineId !== 0 && !pipelines.some(pipeline => pipeline.id === selectedPipelineId)) {
      setSelectedPipelineId(0);
    }
  }, [projects, selectedPipelineId, setAvailablePipelines, setSelectedPipelineId]);

  useEffect(() => {
    setDataFilter(filteredProjectsByCustomFilters);
  }, [filteredProjectsByCustomFilters]);

  useEffect(() => {
    if (currentIndicator?.indicatorKey !== 'PROJECTS_IN_PREPARATION') {
      return;
    }

    const currentPipelineACount = currentIndicator.data?.pipelineACount;
    const currentPipelineBCount = currentIndicator.data?.pipelineBCount;

    if (
      currentPipelineACount === pipelineCounts.pipelineACount &&
      currentPipelineBCount === pipelineCounts.pipelineBCount
    ) {
      return;
    }

    dispatch(
      updateCurrentIndicatorFromDetail({
        ...currentIndicator,
        data: {
          ...(currentIndicator.data || {}),
          pipelineACount: pipelineCounts.pipelineACount,
          pipelineBCount: pipelineCounts.pipelineBCount,
        },
      }),
    );
  }, [currentIndicator, dispatch, pipelineCounts.pipelineACount, pipelineCounts.pipelineBCount]);

  const { handlePaginationChange, handleSort, currentList, pagesCount, currentPage } = usePaginationSort({
    projects: dataFilter,
    defaultSortCriteria: 'title',
    pageCount: rowsPerPage,
  });

  const sortTable = (keyToSort, fieldType) => {
    sortTablesByType(currentList, sortAsc, keyToSort, fieldType);
    setSortAsc(!sortAsc);
  };

  const handleClick = projectCode => {
    const url = `${config.CONVERGENCIA_URL.replace('{project_number}', projectCode.trim())}`;
    window.open(url, '_blank');
  };
  const data = useDataNormalize(headers, selectedKeys, filteredProjectsByCustomFilters);

  const dataExcel = useDataNormalize(headers, selectedKeys, dataFilter);

  const approvalYear = data => extractYearFromExpectedApprovalDate(data) || 'N/A';
  const hasActiveSearch = searchValue.trim().length >= 3;
  const shouldShowEmptyState = dataFilter?.length === 0;
  const visibleColumnCount = tableInfo.filter(th => th.tableHeader !== 'executed_by_or_expected_approval_date').length + 2;

  useEffect(() => {
    setisLoading(true);
    if (data.length > 0) {
      setTimeout(() => {
        setisLoading(false);
      }, 2000);
    }
  }, [data]);

  return (
    <div className="portfolio-simple-indicator-table-container">
      <div className="">
        <TableContainer>
          {loading ? (
            <ProjectsListSkeleton />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <ExcelDownloadButton headers={headers} selectedKeys={selectedKeys} dataExcel={dataExcel} />
                <FilterTables
                  projects={headerFilteredProjects}
                  keysFilter={selectedKeys}
                  fnDataFilter={() => { }}
                  disabledNumber={currentPage}
                  indicatorKey={'PROJECTS_IN_PREPARATION'}
                  onSearchChange={setSearchValue}
                />
              </div>
              <div className=" border-table">
                <Table stickyHeader className="custom-table" style={{ tableLayout: 'auto' }} size="small">
                  <StyledTableHeader>
                    <StyledTableRowHeader className="tableHeader">
                      {shouldShowEmptyState ? (
                        <StyledTableCellHeader colSpan={visibleColumnCount} style={{ height: '88px', padding: '0' }}>
                          <div className="text-head">
                            <p></p>
                          </div>
                        </StyledTableCellHeader>
                      ) : (
                        <>
                          {tableInfo
                            .filter(th => th.tableHeader !== 'executed_by_or_expected_approval_date')
                            .map(th => (
                              <StyledTableCellHeader
                                key={th.tableHeader}
                                align={th.align ? th.align : 'left'}
                                onClick={() =>
                                  sortTable(
                                    th.rootProperty,
                                    th.fieldType,
                                  )
                                }
                                style={{ width: '5%' }}
                                className="code"
                              >
                                <div className="text-head">
                                  {t(th.tableHeader)}
                                  <IconSort className="sortIcon" />
                                </div>
                              </StyledTableCellHeader>
                            ))}
                          <StyledTableCellHeader
                            key="year"
                            align="left"
                            onClick={() => sortTable('field7', 'date')}
                            style={{ width: '3%' }}
                            className="code"
                          >
                            <div className="text-head">
                              {t('year')}
                              <IconSort className="sortIcon" />
                            </div>
                          </StyledTableCellHeader>
                          <StyledTableCellHeader
                            key="pipeline"
                            align="center"
                            onClick={() => sortTable('field6', 'string')}
                            style={{ width: '0%' }}
                            className="code"
                          >
                            <div className="text-head">
                              {t('pipeline')}
                              <IconSort className="sortIcon" />
                            </div>
                          </StyledTableCellHeader>
                        </>
                      )}
                    </StyledTableRowHeader>
                  </StyledTableHeader>
                  <TableBody>
                    {shouldShowEmptyState ? (
                      <StyledTableRow>
                        <StyledTableCell colSpan={visibleColumnCount} className="table-empty-message-cell">
                          {hasActiveSearch ? (
                            <SearchTabErrorMessage search={searchValue} />
                          ) : (
                            <FiltersErrorMessage isTable={true} />
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ) : (
                      currentList?.map((project, index) => (
                        <StyledTableRow
                          key={project.id}
                        >
                          <StyledTableCell align="left" className="link-project">
                            <BlackTooltip
                              title={t('go_to_project_info')}
                              onClick={() => handleClick(project.code)}
                              arrow
                            >
                              <p className={` link-project`}>{project.code}</p>
                            </BlackTooltip>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <BlackTooltip
                              className="name-project-nowrap-tiny"
                              title={project.title}
                              placement="bottom"
                              enterDelay={500}
                              leaveDelay={100}
                              arrow
                            >
                              <p>{project.title}</p>
                            </BlackTooltip>
                          </StyledTableCell>
                          <StyledTableCell align="center">{project.field1}</StyledTableCell>
                          <StyledTableCell align="center">
                            <NumberFormat
                              value={parseInt(project.field2)}
                              displayType={'text'}
                              className={'value'}
                              thousandSeparator={'.'}
                              decimalSeparator={','}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="left">{project.field3 || '-'}</StyledTableCell>
                          <StyledTableCell align="left">{project.field5}</StyledTableCell>
                          <StyledTableCell align="left">{approvalYear(project.field7)}</StyledTableCell>
                          <StyledTableCell align="center">{project.field6}</StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="pagination-style">
                <PaginationTable fnSetRows={setRowsPerPage} rows={rowsPerPage} numProyects={dataFilter?.length} />
                {dataFilter?.length !== 0 ? (
                  <Pagination count={pagesCount} page={currentPage} onChange={handlePaginationChange} />
                ) : null}
              </div>
            </>
          )}
        </TableContainer>
      </div>
    </div>
  );
};

export default ProjectsInPreparationTable;
